import React, { memo, useContext } from "react";
import { defineMessages, IntlContext, useIntl } from "react-intl";
import { chipsMapping } from "../../config/chip/chipConfig.js";
import { Box, Chip, Badge } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { withEmptyCheck } from "../label/EmptyLabel.js";
import "../label/LabelHoc.js";
import { AutoAdjustedWidthTooltip } from "../tooltip/AutoAdjustedWidthTooltip.js";
import { withAutoAdjustedComplexTooltip } from "../tooltip/AutoAdjustedComplexTooltip.js";
import { StyledTooltip } from "../tooltip/Tooltip.js";
import "../tooltip/MultiLineAutoAdjustedWidthTooltip.js";
import { Chip as Chip$1 } from "./Chips.js";
import { withLabelChipTooltip, withChipTooltip } from "./ChipTooltipHoc.js";
import { useChipValue } from "./ChipValue.hooks.js";
const localized = defineMessages({
  chipIdLabel: {
    id: "chipIdLabel",
    defaultMessage: "ID"
  }
});
const useStyles = makeStyles()((theme) => ({
  chipContainer: {
    columnGap: theme.spacing(2),
    display: "flex",
    overflow: "hidden",
    width: "fit-content"
  },
  chipBadge: {
    marginRight: theme.spacing(2),
    "& .MuiBadge-anchorOriginTopRightRectangular": {
      top: `${theme.spacing(1.5)} !important`
    }
  },
  idChip: {
    ...theme.typography.subtitle2,
    height: theme.custom.chipHeight,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.custom.palette.content.liner}`,
    borderRadius: 100,
    "& .MuiChip-avatar": {
      color: theme.palette.secondary.main,
      display: "flex",
      justifyContent: "center",
      alignItems: "center"
    },
    "& .MuiChip-label": {
      paddingLeft: theme.spacing(0.5),
      fontWeight: 500
    }
  }
}));
const MemoizedLabelChip = withEmptyCheck(
  withLabelChipTooltip(
    withAutoAdjustedComplexTooltip(
      memo(({ value }) => {
        const { classes } = useStyles();
        const { formatMessage } = useContext(IntlContext);
        return /* @__PURE__ */ React.createElement(Box, { className: classes.chipContainer }, /* @__PURE__ */ React.createElement(Chip, { className: classes.idChip, avatar: /* @__PURE__ */ React.createElement("span", null, formatMessage(localized.chipIdLabel)), label: value }));
      })
    )
  )
);
const MemoizedChip = withEmptyCheck(
  withChipTooltip(
    withAutoAdjustedComplexTooltip(
      memo(({ value, label, chipName, cursorPointer, onDelete }) => {
        const { classes } = useStyles();
        const { chip, chipLabel } = useChipValue(value, chipName, label);
        return chip ? /* @__PURE__ */ React.createElement(Box, { className: classes.chipContainer }, /* @__PURE__ */ React.createElement(
          Chip$1,
          {
            cursorPointer,
            startIconClass: chip.startIconClass,
            chipType: chip.chipType,
            chipVariant: chip.chipVariant,
            label: chipLabel,
            onDelete
          }
        )) : /* @__PURE__ */ React.createElement(AutoAdjustedWidthTooltip, { value: chipLabel });
      })
    )
  )
);
withEmptyCheck(
  memo(({ value, chipName }) => {
    const { classes } = useStyles();
    const { formatMessage } = useIntl();
    const chips = chipsMapping[chipName];
    return /* @__PURE__ */ React.createElement(Box, { className: classes.chipContainer }, /* @__PURE__ */ React.createElement(
      StyledTooltip,
      {
        element: /* @__PURE__ */ React.createElement(Box, { display: "flex" }, /* @__PURE__ */ React.createElement(Badge, { className: classes.chipBadge, badgeContent: value?.length, color: "default" }), /* @__PURE__ */ React.createElement(MemoizedChip, { value: value[0], chipName })),
        title: value?.map((i) => /* @__PURE__ */ React.createElement(Box, null, chips[i].labelTypeString ? chips[i].label : formatMessage(chips[i].label)))
      }
    ));
  })
);
const MemoizedDefaultChip = withEmptyCheck(
  withLabelChipTooltip(
    withAutoAdjustedComplexTooltip(
      memo(({ value, variant, type, className, startIconClass }) => {
        const { classes } = useStyles();
        return /* @__PURE__ */ React.createElement(Box, { className: `${classes.chipContainer} ${className ?? ""}` }, /* @__PURE__ */ React.createElement(Chip$1, { chipType: type, chipVariant: variant, label: value, startIconClass }));
      })
    )
  )
);
const renderDefaultChip = (value, variant, type) => {
  return /* @__PURE__ */ React.createElement(MemoizedDefaultChip, { value, variant, type });
};
const renderChip = (value, type) => {
  return /* @__PURE__ */ React.createElement(MemoizedChip, { value, chipName: type });
};
export {
  MemoizedChip,
  MemoizedDefaultChip,
  MemoizedLabelChip,
  renderChip,
  renderDefaultChip
};
//# sourceMappingURL=ChipRenderer.js.map
